import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Phone, ShieldCheck, RotateCcw, ChevronLeft, Sun } from "lucide-react";

function normalisePhone(raw) {
  const p = (raw || "").trim().replace(/[\s\-()]/g, "");
  if (!p) return "";
  if (p.startsWith("+")) return p;
  const digits = p.replace(/\D/g, "");
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
  return digits ? `+${digits}` : "";
}

export default function CustomerLogin() {
  const nav = useNavigate();
  const { otpLogin } = useAuth();

  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Capture a `?ref=CODE` referral deep-link so Buy New Installation can
  // auto-fill it later, even after the OTP login round-trip.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("purvodaya_ref_code", ref.trim().toUpperCase());
  }, []);

  const e164 = useMemo(() => normalisePhone(phone), [phone]);
  const phoneValid = e164.length >= 12 && e164.length <= 15;

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  async function requestOtp() {
    if (!phoneValid) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setSending(true);
    try {
      const r = await api.post("/customer-auth/otp/request", { phone: e164 });
      setDelivery(r.data?.delivery || "sms");
      setSecondsLeft(r.data?.expires_in_seconds || 300);
      setStep(2);
      toast.success(r.data?.delivery === "sms" ? "OTP sent via SMS" : "OTP generated (mock mode)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  }

  async function verifyOtp(e) {
    e?.preventDefault();
    if (code.trim().length < 4) { toast.error("Enter the 6-digit OTP"); return; }
    setVerifying(true);
    try {
      await otpLogin(e164, code.trim());
      toast.success("Welcome to Purvodaya Solar!");
      const { data } = await api.get("/customer-app/onboarding/status");
      nav(data.needs_onboarding ? "/app/onboarding" : "/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setVerifying(false); }
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] relative overflow-hidden" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
      <div className="pointer-events-none absolute -top-28 -right-20 w-72 h-72 rounded-full bg-[#FEF3C7] blur-3xl opacity-70"></div>
      <div className="pointer-events-none absolute top-1/2 -left-28 w-72 h-72 rounded-full bg-[#DCFCE7] blur-3xl opacity-70"></div>

      <div className="relative max-w-md mx-auto min-h-screen flex flex-col px-6 pt-10 pb-8">
        <div className="flex items-center gap-2.5">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] flex items-center justify-center shadow-[0_4px_14px_0_rgba(13,71,33,0.25)]">
            <Sun className="w-6 h-6 text-amber-300" strokeWidth={2} />
          </div>
          <div className="text-lg font-extrabold tracking-tight text-[#0F172A]">Purvodaya Solar</div>
        </div>

        <div className="mt-9 flex items-center gap-2">
          <div className={"h-1.5 flex-1 rounded-full " + (step >= 1 ? "bg-[#F59E0B]" : "bg-slate-200")}></div>
          <div className={"h-1.5 flex-1 rounded-full " + (step >= 2 ? "bg-[#F59E0B]" : "bg-slate-200")}></div>
        </div>
        <div className="mt-2 text-xs uppercase font-bold tracking-wider text-slate-400">
          Step {step} of 2 — {step === 1 ? "Phone" : "Verify OTP"}
        </div>

        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] mt-5 leading-tight">
          {step === 1 ? "Your solar journey, in your pocket." : "Verify it's you."}
        </h1>
        <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
          {step === 1
            ? "Track installations, raise complaints, and go solar again — all with just your mobile number. No password needed."
            : <>OTP sent to <span className="font-mono text-[#0F172A]">{e164}</span>. {delivery === "mock" ? "Test mode — SMS delivery is simulated." : "Check your SMS inbox."}</>}
        </p>

        <div className="mt-8 flex-1">
          {step === 1 && (
            <form onSubmit={(e) => { e.preventDefault(); requestOtp(); }} className="space-y-5" data-testid="app-login-step-phone">
              <div>
                <label className="block text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">Mobile number</label>
                <div className="flex items-center gap-2 bg-white rounded-xl border-2 border-slate-200 focus-within:border-[#F59E0B] px-4 py-3.5 transition-colors">
                  <Phone className="w-4 h-4 text-[#0D4721] flex-shrink-0" />
                  <span className="font-mono text-sm text-slate-400">+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    data-testid="app-login-phone"
                    placeholder="98765 43210"
                    className="flex-1 bg-transparent outline-none text-lg font-mono tracking-wider placeholder-slate-300 text-[#0F172A]"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={!phoneValid || sending}
                data-testid="app-login-send-otp"
                className="w-full bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] text-white py-4 rounded-xl text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2.5 disabled:opacity-40 active:scale-[0.98] transition-transform shadow-[0_4px_14px_0_rgba(13,71,33,0.25)]"
              >
                {sending ? "Sending OTP…" : "Send OTP"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyOtp} className="space-y-5" data-testid="app-login-step-otp">
              <div>
                <label className="block text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">6-digit OTP</label>
                <div className="flex items-center gap-2 bg-white rounded-xl border-2 border-slate-200 focus-within:border-[#F59E0B] px-4 py-3 transition-colors">
                  <ShieldCheck className="w-4 h-4 text-[#0D4721] flex-shrink-0" />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="app-login-otp"
                    placeholder="••••••"
                    className="flex-1 bg-transparent outline-none text-2xl tracking-[0.6em] font-mono text-center text-[#0F172A] placeholder-slate-300"
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-xs text-slate-500 font-mono">
                    {secondsLeft > 0 ? `Expires in ${Math.floor(secondsLeft / 60)}m ${(secondsLeft % 60).toString().padStart(2, "0")}s` : "OTP expired"}
                  </div>
                  <button
                    type="button"
                    onClick={requestOtp}
                    disabled={sending || secondsLeft > 250}
                    data-testid="app-login-resend-otp"
                    className="text-xs uppercase tracking-wider font-bold text-[#D97706] disabled:opacity-30 inline-flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" /> Resend
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={verifying || code.length < 4}
                data-testid="app-login-verify-otp"
                className="w-full bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] text-white py-4 rounded-xl text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2.5 disabled:opacity-40 active:scale-[0.98] transition-transform shadow-[0_4px_14px_0_rgba(13,71,33,0.25)]"
              >
                {verifying ? "Verifying…" : "Verify & Continue"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>

              <button
                type="button"
                onClick={() => { setStep(1); setCode(""); }}
                data-testid="app-login-change-phone"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-slate-500 hover:text-slate-800"
              >
                <ChevronLeft className="w-3 h-3" /> Change phone number
              </button>
            </form>
          )}
        </div>

        <p className="text-[11px] text-slate-400 leading-relaxed mt-6">
          By continuing you agree to receive transactional SMS from Purvodaya Solar. Standard carrier rates may apply.
        </p>
      </div>
    </div>
  );
}
